import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { PivotChart } from "../../../../components/Exports/Builder/components/PivotChart";
import { PivotGrid } from "../../../../components/Exports/Builder/components/PivotGrid";
import { useBuilderDims } from "../../../../components/Exports/Builder/hooks/useBuilderDims";
import { materializeView } from "../../../../components/Exports/Builder/utils/materializeView";
import { pivotHasData } from "../../../../components/Exports/Builder/utils/pivot";
import { serializeView, deserializeView } from "../../../../components/Exports/Builder/utils/viewState";
import {
  seriesOptions,
  TOTAL_SERIES,
} from "../../../../components/Exports/Builder/utils/pivotToChartData";
import {
  DATE_PRESETS,
  computePresetRange,
} from "../../../../components/Exports/Builder/utils/datePresets";
import { getBuilderViews } from "../../../../apis/apiEQL";
import {
  freezePieceResult,
  pivotMetricOptions,
  pivotMetricText,
} from "../utils/pivotSnapshot";

const CHART_TYPES = ["donut", "pie", "bar", "line", "nightingale", "rose"];
const KINDS = [
  { value: "chart", label: "Gráfica" },
  { value: "table", label: "Tabla" },
  { value: "indicator", label: "Indicador" },
];

// Replaces the `created_at` bounds inside a saved view's EQL filter tree, keeping
// every other condition. Mirrors how ReportFilters emits gte/lte on created_at.
const applyDateRange = (filters, after, before) => {
  const base = filters?.AND ? filters.AND : filters ? [filters] : [];
  const conds = base.filter((c) => c?.field !== "created_at");
  if (after) conds.push({ field: "created_at", operator: "gte", value: after });
  if (before) conds.push({ field: "created_at", operator: "lte", value: before });
  return conds.length ? { AND: conds } : undefined;
};

const ORIGINAL = "__original__";

/**
 * Adds a dashboard piece FROM a saved Builder view. The heavy lifting (choosing
 * dimensions, chart type, filters) stays in the Builder; here you only pick a
 * saved view and optionally override its date range, then freeze the result.
 */
export const PieceEditor = ({ open, onClose, onSave, initial }) => {
  const [t] = useTranslation("exports");
  const navigate = useNavigate();
  const { dimByKey, fieldTitle, labelsRef } = useBuilderDims();

  const [views, setViews] = useState([]);
  const [loadingViews, setLoadingViews] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState(
    initial?.sourceRef?.viewId || ""
  );
  const [datePreset, setDatePreset] = useState(ORIGINAL);
  const [customAfter, setCustomAfter] = useState("");
  const [customBefore, setCustomBefore] = useState("");
  const [title, setTitle] = useState(initial?.title || "");

  // How to represent the view here (independent of how it was saved), plus the
  // chart type and — for indicators — which pivot total to show.
  const [kind, setKind] = useState(initial?.kind || "chart");
  const [chartType, setChartType] = useState(
    initial?.sourceRef?.state?.chartType || "donut"
  );
  // Indicators: pick one OR MANY totals — each becomes its own indicator piece.
  const [selectedMetrics, setSelectedMetrics] = useState(
    initial?.sourceRef?.metricKey ? [initial.sourceRef.metricKey] : ["grand"]
  );
  // Which column slice to plot when the view is a rows × cols cross.
  const [seriesKey, setSeriesKey] = useState(
    initial?.sourceRef?.state?.seriesKey || TOTAL_SERIES
  );

  const [pivot, setPivot] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const reqId = useRef(0);

  // Load the user's saved Builder views when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoadingViews(true);
    (async () => {
      try {
        const resp = await getBuilderViews();
        if (active) setViews(resp?.data || []);
      } catch (e) {
        console.error("PieceEditor: could not load saved views", e);
      } finally {
        if (active) setLoadingViews(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open]);

  const selectedView = views.find((v) => v.view_id === selectedViewId);

  // Base state = the chosen saved view, or (when editing) the piece's own stored
  // source view if its origin view no longer exists.
  const baseState = useMemo(() => {
    if (selectedView?.state) return deserializeView(selectedView.state);
    if (initial?.sourceRef?.state) return deserializeView(initial.sourceRef.state);
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedViewId, views, initial]);

  // Seed the representation from the view ONLY on a real view switch — not on
  // mount or when the views list loads (which would clobber an edited piece's
  // saved kind/metric).
  const prevViewId = useRef(selectedViewId);
  useEffect(() => {
    if (prevViewId.current === selectedViewId) return;
    prevViewId.current = selectedViewId;
    const v = views.find((x) => x.view_id === selectedViewId);
    if (!v?.state) return;
    const s = deserializeView(v.state);
    setKind(s.view === "chart" ? "chart" : "table");
    setChartType(s.chartType || "donut");
    setSeriesKey(s.seriesKey || TOTAL_SERIES);
    setSelectedMetrics(["grand"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedViewId, views]);

  const resolveRange = () => {
    if (datePreset === ORIGINAL) return null; // keep the view's own range
    if (datePreset === "custom") return { after: customAfter, before: customBefore };
    return computePresetRange(datePreset);
  };

  const effectiveFilters = useMemo(() => {
    if (!baseState) return undefined;
    const range = resolveRange();
    if (!range) return baseState.appliedFilters;
    return applyDateRange(baseState.appliedFilters, range.after, range.before);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseState, datePreset, customAfter, customBefore]);

  const canQuery =
    !!baseState &&
    ((baseState.assignments.rows?.length || 0) +
      (baseState.assignments.cols?.length || 0) >
      0);

  const sig = useMemo(
    () => JSON.stringify({ v: selectedViewId, f: effectiveFilters ?? null }),
    [selectedViewId, effectiveFilters]
  );

  useEffect(() => {
    if (!open || !canQuery) {
      setPivot(null);
      setMeta(null);
      return;
    }
    const id = ++reqId.current;
    const stale = () => id !== reqId.current;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const state = { ...baseState, appliedFilters: effectiveFilters };
        const { pivot: p, meta: m } = await materializeView(state, {
          dimByKey,
          labels: labelsRef.current,
        });
        if (stale()) return;
        setPivot(p);
        setMeta(m);
      } catch (e) {
        if (stale()) return;
        console.error("PieceEditor: preview failed", e);
        setError(true);
        setPivot(null);
      } finally {
        if (!stale()) setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, open]);

  const hasData = pivotHasData(pivot);

  // Indicator metrics available for the current pivot; clamp the selection if a
  // date change dropped the previously chosen row/cell.
  const metricOptions = useMemo(() => pivotMetricOptions(pivot), [pivot]);
  const metricLabelOf = (key) =>
    metricOptions.find((o) => o.value === key)?.label || key;
  // Drop any selection no longer available (e.g. a date change removed a row).
  const effectiveMetrics = useMemo(() => {
    const valid = selectedMetrics.filter((k) =>
      metricOptions.some((o) => o.value === k)
    );
    return valid.length ? valid : ["grand"];
  }, [selectedMetrics, metricOptions]);

  // Chart series: TOTAL plus each column of the cross (empty when there is no
  // real column axis). Rows are always the chart's categories.
  const seriesChoices = useMemo(
    () => seriesOptions(pivot, t("builder.total")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pivot]
  );
  const effectiveSeriesKey = seriesChoices.some((s) => s.value === seriesKey)
    ? seriesKey
    : TOTAL_SERIES;

  const handleSave = () => {
    if (!pivot || !baseState) return;
    const used = [
      ...(baseState.assignments.rows || []),
      ...(baseState.assignments.cols || []),
    ];
    const fieldTitles = Object.fromEntries(used.map((k) => [k, fieldTitle(k)]));

    // The materialized source view + adjusted date, shared by every piece built
    // from this dialog (persisted so Refresh re-freezes identically).
    const state = serializeView({
      appliedFilters: effectiveFilters,
      assignments: baseState.assignments,
      filterValues: baseState.filterValues,
      depthByCatalog: baseState.depthByCatalog,
      view: baseState.view,
      chartType,
      seriesKey: effectiveSeriesKey,
      valueMode: baseState.valueMode,
      measure: baseState.measure,
      stat: baseState.stat,
    });
    const baseSource = {
      state,
      viewId: selectedViewId || initial?.sourceRef?.viewId || null,
      viewName: selectedView?.name || initial?.sourceRef?.viewName || null,
      capturedAt: new Date().toISOString(),
      reportsTotal: meta?.total ?? pivot.grandTotal,
    };

    if (kind === "indicator") {
      // One indicator piece per selected total — the whole point of the tags
      // multi-select is not repeating this dialog N times.
      const pieces = effectiveMetrics.map((mk) => {
        const label = metricLabelOf(mk);
        return {
          title: label,
          kind: "indicator",
          result: freezePieceResult(pivot, {
            kind: "indicator",
            valueMode: baseState.valueMode,
            metricKey: mk,
            metricLabel: label,
          }),
          sourceRef: { ...baseSource, metricKey: mk, metricLabel: label },
        };
      });
      onSave(pieces);
      return;
    }

    const finalTitle =
      title.trim() || selectedView?.name || initial?.title || "Pieza";
    onSave({
      title: finalTitle,
      kind,
      result: freezePieceResult(pivot, {
        kind,
        chartType,
        seriesKey: effectiveSeriesKey,
        valueMode: baseState.valueMode,
        title: finalTitle,
        fieldTitles,
      }),
      sourceRef: { ...baseSource, metricKey: null, metricLabel: null },
    });
  };

  const noViews = !loadingViews && views.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { minHeight: "78vh" } }}
    >
      <DialogTitle sx={{ fontSize: "1.05rem", fontWeight: 600 }}>
        {initial ? "Editar pieza" : "Agregar pieza"}
      </DialogTitle>
      <DialogContent dividers>
        {noViews ? (
          <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              No tienes vistas guardadas. Crea y guarda tus gráficas/tablas en el
              Builder y aquí podrás insertarlas.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem" }}
              onClick={() => {
                onClose();
                navigate("/exports/builder");
              }}
            >
              Ir al Builder
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Controls: pick a saved view + adjust the date range */}
            <Grid size={12}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "flex-start",
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Vista guardada"
                  value={selectedViewId}
                  onChange={(e) => setSelectedViewId(e.target.value)}
                  sx={{ minWidth: 220, flexGrow: 1 }}
                  helperText={loadingViews ? "Cargando vistas…" : undefined}
                >
                  {views.map((v) => (
                    <MenuItem key={v.view_id} value={v.view_id}>
                      {v.name}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Represent the view as chart / table / indicator */}
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={kind}
                  onChange={(_e, v) => v && setKind(v)}
                  disabled={!baseState}
                  sx={{
                    alignSelf: "center",
                    "& .MuiToggleButton-root": { fontSize: "0.7rem" },
                  }}
                >
                  {KINDS.map((k) => (
                    <ToggleButton key={k.value} value={k.value}>
                      {k.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {kind === "chart" && (
                  <TextField
                    select
                    size="small"
                    label="Tipo de gráfica"
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value)}
                    sx={{ minWidth: 180 }}
                    disabled={!baseState}
                  >
                    {CHART_TYPES.map((ct) => (
                      <MenuItem key={ct} value={ct}>
                        {t(`builder.chart_${ct}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {kind === "chart" && seriesChoices.length > 0 && (
                  <TextField
                    select
                    size="small"
                    label="Serie (columna a graficar)"
                    value={effectiveSeriesKey}
                    onChange={(e) => setSeriesKey(e.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {seriesChoices.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {kind === "indicator" && (
                  <TextField
                    select
                    size="small"
                    label="Indicadores (uno o varios)"
                    value={effectiveMetrics}
                    onChange={(e) =>
                      setSelectedMetrics(
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value
                      )
                    }
                    sx={{ minWidth: 280, flexGrow: 1 }}
                    disabled={!hasData}
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((v) => (
                            <Chip
                              key={v}
                              size="small"
                              label={metricLabelOf(v)}
                              sx={{ height: 20, "& .MuiChip-label": { fontSize: "0.68rem", px: 0.75 } }}
                            />
                          ))}
                        </Box>
                      ),
                    }}
                    helperText={
                      !hasData
                        ? undefined
                        : "Elige uno o varios: total general, de fila o de columna"
                    }
                  >
                    {metricOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <TextField
                  select
                  size="small"
                  label="Rango de fechas"
                  value={datePreset}
                  onChange={(e) => setDatePreset(e.target.value)}
                  sx={{ minWidth: 200 }}
                  disabled={!baseState}
                >
                  <MenuItem value={ORIGINAL}>Original de la vista</MenuItem>
                  {DATE_PRESETS.map((key) => (
                    <MenuItem key={key} value={key}>
                      {t(`builder.preset_${key}`)}
                    </MenuItem>
                  ))}
                </TextField>

                {datePreset === "custom" && (
                  <Box sx={{ display: "flex", gap: 1, minWidth: 320 }}>
                    <TextField
                      type="date"
                      size="small"
                      label={t("builder.date_from")}
                      value={customAfter}
                      onChange={(e) => setCustomAfter(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                    <TextField
                      type="date"
                      size="small"
                      label={t("builder.date_to")}
                      value={customBefore}
                      onChange={(e) => setCustomBefore(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Box>
                )}

                {kind !== "indicator" && (
                  <TextField
                    size="small"
                    label="Título de la pieza"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    sx={{ minWidth: 240, flexGrow: 1 }}
                  />
                )}
              </Box>
            </Grid>

            {/* Preview — full width, below the controls */}
            <Grid size={12}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ minHeight: 440 }}>
                {!baseState ? (
                  <Typography color="text.secondary">
                    Elige una vista guardada para previsualizar.
                  </Typography>
                ) : loading ? (
                  <Typography color="text.secondary">Calculando vista previa…</Typography>
                ) : error ? (
                  <Typography color="error">No se pudo calcular la vista previa.</Typography>
                ) : !hasData ? (
                  <Typography color="text.secondary">
                    Sin resultados para este rango.
                  </Typography>
                ) : kind === "indicator" ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {effectiveMetrics.map((mk) => (
                      <Box
                        key={mk}
                        className="dyTheme1 dyBorder1 rounded"
                        sx={{ p: 3, textAlign: "center", minWidth: 200, flexGrow: 1 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {metricLabelOf(mk)}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {pivotMetricText(pivot, mk)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : kind === "table" ? (
                  <PivotGrid pivot={pivot} fieldTitle={fieldTitle} valueMode={baseState.valueMode} />
                ) : (
                  <PivotChart
                    pivot={pivot}
                    type={chartType}
                    seriesKey={effectiveSeriesKey}
                    // Not the live `title`: EChart only uses it as the series
                    // name, yet it's an option dep, so every keystroke replayed
                    // the chart. The typed title is applied on save.
                    title={selectedView?.name || initial?.title}
                    valueMode={baseState.valueMode}
                    // Same rule as the saved piece, so the preview matches it.
                    hideZeros
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" sx={{ fontSize: "0.7rem" }} onClick={onClose}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          sx={{ fontSize: "0.7rem" }}
          onClick={handleSave}
          disabled={!hasData || loading}
        >
          {initial ? "Guardar cambios" : "Agregar al panel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PieceEditor;
